import {
  Box,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  Rating,
  Divider,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Checkbox,
  Fab,
  Snackbar,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import React, { useState, useMemo } from 'react';

import { useGetVendorsBySearchId } from '@/api/endpoints/vendor/useGetVendorsBySearchId';
import { VendorSearchResult } from '@/api/models/vendor';
import { ArrowDownIcon } from '@/assets/icons/ArrowDownIcon';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { ArrowUpIcon } from '@/assets/icons/ArrowUpIcon';
import { Edit05Icon } from '@/assets/icons/Edit05Icon';
import { Globe02Icon } from '@/assets/icons/Globe02Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { Phone01Icon } from '@/assets/icons/Phone01Icon';
import { PhoneXIcon } from '@/assets/icons/PhoneXIcon';
import { SearchLgIcon } from '@/assets/icons/SearchLgIcon';
import { ShieldTickIcon } from '@/assets/icons/ShieldTickIcon';
import { UserX01Icon } from '@/assets/icons/UserX01Icon';
import { ZapFastIcon } from '@/assets/icons/ZapFastIcon';

import BaseLottie from '@/components/lottie/BaseLottie';
import HowWeRankVendors from '@/components/vendor/HowWeRankVendors';

// eslint-disable-next-line @nx/enforce-module-boundaries
import vendorSearchAnimation from '../../../public/assets/images/vendor_search.json';

export interface VendorSearchResultsProps {
  vendors: VendorSearchResult[];
  searchQuery: string;
  address: string;
  disclaimer?: string;
  search_query?: string;
  searchId?: string;
  isAuthenticated: boolean;
  onNewSearch: () => void;
  onEditSearch?: (newQuery: string) => void;
  onCallVendors: (selectedVendors: string[], vendorDetails: VendorSearchResult[]) => Promise<void>;
  onAuthRequired?: () => void;
  isCallInProgress?: boolean;
  showBackground?: boolean;
  containerMaxWidth?: 'lg' | 'xl' | 'md';
  wrapperStyles?: object;
  calledVendors?: string[];
  initialSelectedVendors?: string[]; // Pre-selected vendors (e.g., from before login)
}

const VendorSearchResults: React.FC<VendorSearchResultsProps> = ({
  vendors = [],
  searchQuery = '',
  address = '',
  disclaimer,
  search_query,
  searchId,
  isAuthenticated,
  onNewSearch,
  onEditSearch,
  onCallVendors,
  onAuthRequired,
  isCallInProgress = false,
  showBackground = true,
  containerMaxWidth = 'lg',
  wrapperStyles = {},
  calledVendors: externalCalledVendors = [],
  initialSelectedVendors = [],
}) => {
  const [sortBy, setSortBy] = useState<'match_score' | 'distance' | 'name'>('distance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedVendors, setSelectedVendors] = useState<string[]>(initialSelectedVendors);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [isEditingSearch, setIsEditingSearch] = useState(false);
  const [editedSearchQuery, setEditedSearchQuery] = useState(searchQuery);

  // Fetch stored vendor data with calculated distances if we have a searchId
  const { data: storedVendors } = useGetVendorsBySearchId(searchId || '', !!searchId);

  // Merge original vendor data with stored data to get calculated distances
  const enhancedVendors = useMemo(() => {
    if (!storedVendors || !searchId) {
      return vendors; // Use original data if no stored data available
    }

    return vendors.map(originalVendor => {
      // Find matching stored vendor by name and address
      const storedVendor = storedVendors.find(
        stored => stored.name === originalVendor.name && stored.address === originalVendor.address
      );

      if (storedVendor) {
        // Merge calculated distance fields
        return {
          ...originalVendor,
          distance_meters: storedVendor.distance_meters,
          distance_formatted: storedVendor.distance_formatted,
          distance_miles: storedVendor.distance_miles,
        };
      }

      return originalVendor; // Return original if no stored match found
    });
  }, [vendors, storedVendors, searchId]);

  const parseDistance = (vendor: VendorSearchResult): number => {
    // Use accurate distance_miles if available, otherwise parse the distance string
    if (vendor.distance_miles !== undefined && vendor.distance_miles !== null) {
      return vendor.distance_miles;
    }

    // Handle "Local service area" - treat as very close (0.1 miles for sorting)
    if (
      vendor.distance &&
      (vendor.distance.toLowerCase().includes('local') ||
        vendor.distance.toLowerCase().includes('service area'))
    ) {
      return 0.1; // Very close for sorting purposes
    }

    const match = vendor.distance.match(/([0-9.]+)/);
    return match ? parseFloat(match[1]) : 999; // High number for unknown distances (sort to end)
  };

  const formatDistanceDisplay = (vendor: VendorSearchResult): string => {
    // Always prefer calculated distances over AI-provided strings
    if (vendor.distance_formatted) {
      return vendor.distance_formatted;
    }
    if (vendor.distance_miles !== undefined && vendor.distance_miles !== null) {
      return `${vendor.distance_miles.toFixed(1)} mi`;
    }

    // Handle "Local service area" - show as nearby
    if (
      vendor.distance &&
      (vendor.distance.toLowerCase().includes('local') ||
        vendor.distance.toLowerCase().includes('service area'))
    ) {
      return 'Nearby'; // More user-friendly than "Local service area"
    }

    // Handle other distance strings that contain numbers
    if (vendor.distance && vendor.distance !== 'null' && /\d/.test(vendor.distance)) {
      return vendor.distance;
    }

    // Final fallback
    return 'Distance calculating...';
  };

  const sortedResults = useMemo(() => {
    const sorted = [...enhancedVendors].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
      case 'match_score':
        comparison = a.match_score - b.match_score;
        break;
      case 'distance':
        comparison = parseDistance(a) - parseDistance(b);
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      default:
        comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [enhancedVendors, sortBy, sortOrder]);

  const handleSortChange = (field: 'match_score' | 'distance' | 'name') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'name' || field === 'distance' ? 'asc' : 'desc');
    }
  };

  const getSortIcon = (field: 'match_score' | 'distance' | 'name') => {
    if (sortBy !== field) return undefined;
    const IconComponent = sortOrder === 'desc' ? ArrowDownIcon : ArrowUpIcon;
    return <IconComponent width={16} height={16} />;
  };

  const handleVendorSelection = (vendorId: string, isChecked: boolean) => {
    setSelectedVendors(prev => {
      if (isChecked) {
        return [...prev, vendorId];
      }
      return prev.filter(id => id !== vendorId);
    });
  };

  const handleCallVendorsClick = async () => {
    if (selectedVendors.length === 0) {
      setSnackbarMessage('Please select at least one provider to call');
      return;
    }

    // Check if authentication is required
    if (!isAuthenticated && onAuthRequired) {
      onAuthRequired();
      return;
    }

    // Get selected vendor details
    const selectedVendorDetails = sortedResults.filter(vendor => {
      const vendorId = `${vendor.name}-${vendor.address}`;
      return selectedVendors.includes(vendorId);
    });

    try {
      await onCallVendors(selectedVendors, selectedVendorDetails);
      // Clear selection after successful call initiation
      setSelectedVendors([]);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleEditSearch = () => {
    setIsEditingSearch(true);
    setEditedSearchQuery(searchQuery);
  };

  const handleCancelEdit = () => {
    setIsEditingSearch(false);
    setEditedSearchQuery(searchQuery);
  };

  const handleSearchSubmit = () => {
    if (editedSearchQuery.trim() && onEditSearch) {
      onEditSearch(editedSearchQuery.trim());
      setIsEditingSearch(false);
    }
  };

  const defaultWrapperStyles = showBackground
    ? {
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #FFF9F5 0%, #FFFFFF 100%)',
      py: { xs: 4, md: 6 },
    }
    : { py: { xs: 2, md: 3 } };

  return (
    <Box sx={{ ...defaultWrapperStyles, ...wrapperStyles }}>
      <Container maxWidth={containerMaxWidth}>
        <Stack spacing={4}>
          {/* Loading Animation - same as VendorSearchSection */}
          {isCallInProgress && (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                width: '100%',
                maxWidth: '800px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                mx: 'auto',
              }}
            >
              <Box sx={{ width: 200, height: 200 }}>
                <BaseLottie animationData={vendorSearchAnimation} loop autoplay />
              </Box>
              <Stack spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Deploying our AI agent to scout the perfect providers...
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Our digital detective is on the case! Sit tight while we match you with top
                  providers tailored to your needs.
                </Typography>
              </Stack>
            </Paper>
          )}

          {/* Main Content - hidden when loading */}
          {!isCallInProgress && (
            <>
              {/* No-Spam Hero Section */}
              <Paper
                elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              >
                <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <ShieldTickIcon width={32} height={32} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                        Your Privacy Protected
                      </Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ opacity: 0.95, lineHeight: 1.4 }}>
                      No more spam calls or endless emails! We handle all provider communication 
                      so you can get quotes without giving out your personal information.
                    </Typography>
                  </Stack>
                  
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={3}
                    sx={{ width: '100%' }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.15)',
                          }}
                        >
                          <PhoneXIcon width={24} height={24} />
                        </Box>
                        <Stack>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                            No Spam Calls
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Keep your phone number private
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.15)',
                          }}
                        >
                          <Mail01Icon width={24} height={24} />
                        </Box>
                        <Stack>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                            No Spam Emails
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Your inbox stays clean
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.15)',
                          }}
                        >
                          <UserX01Icon width={24} height={24} />
                        </Box>
                        <Stack>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                            Complete Privacy
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            We never share your details
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>

              {/* Header */}
              <Stack spacing={3}>
                <Stack direction="row" spacing={2}>
                  <Button
                    startIcon={<ArrowLeftIcon width={20} height={20} />}
                    onClick={onNewSearch}
                    sx={{
                      width: 'fit-content',
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    New Search
                  </Button>
                  {onEditSearch && (
                    <Button
                      startIcon={<Edit05Icon width={20} height={20} />}
                      onClick={handleEditSearch}
                      sx={{
                        width: 'fit-content',
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      Edit Search
                    </Button>
                  )}
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Provider Search Results
                  </Typography>
                  {isEditingSearch ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        value={editedSearchQuery}
                        onChange={e => setEditedSearchQuery(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleSearchSubmit();
                          }
                        }}
                        variant="outlined"
                        size="small"
                        placeholder="Enter search query..."
                        sx={{ flex: 1, maxWidth: 400 }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchLgIcon width={20} height={20} />
                              </InputAdornment>
                            ),
                          },
                        }}
                        autoFocus
                      />
                      <Button
                        variant="contained"
                        onClick={handleSearchSubmit}
                        disabled={!editedSearchQuery.trim()}
                        size="small"
                      >
                        Search
                      </Button>
                      <Button variant="outlined" onClick={handleCancelEdit} size="small">
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <>
                      <Typography variant="body1" color="text.secondary">
                        Found {vendors.length} provider{vendors.length !== 1 ? 's' : ''} for "
                        {searchQuery}"
                      </Typography>
                      {address && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MarkerPin01Icon width={16} height={16} />
                          <Typography variant="body2" color="text.secondary">
                            Near {address}
                          </Typography>
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
              </Stack>

              {/* Disclaimer and Search Query Info */}
              {(disclaimer || search_query) && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Stack spacing={1}>
                    {disclaimer && <Typography variant="body2">{disclaimer}</Typography>}
                    {search_query && search_query !== searchQuery && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Search refined to: "{search_query}"
                      </Typography>
                    )}
                  </Stack>
                </Alert>
              )}

              {/* Benefits Highlight */}
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'success.50',
                  border: '1px solid',
                  borderColor: 'success.200',
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
                icon={<ShieldTickIcon width={24} height={24} />}
              >
                <Stack spacing={1}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ✨ Get quotes without the hassle!
                  </Typography>
                  <Typography variant="body2">
                    Terri handles all communication with providers so you never have to deal 
                    with aggressive sales calls, spam emails, or pushy follow-ups. Your contact 
                    information stays completely private.
                  </Typography>
                </Stack>
              </Alert>

              {/* How We Rank Providers Section - Collapsible */}
              <HowWeRankVendors />

              {/* Provider Status Summary */}
              {externalCalledVendors.length > 0 && (
                <Alert
                  severity="success"
                  variant="outlined"
                  sx={{ mb: 2, textAlign: 'center', alignItems: 'center', display: 'flex' }}
                  icon={<Phone01Icon width={20} height={20} />}
                >
                  <Typography variant="body2">
                    <strong>
                      Terri is reaching out to {externalCalledVendors.length} provider
                      {externalCalledVendors.length > 1 ? 's' : ''}
                    </strong>{' '}
                    to get quotes for you. You'll receive updates as quotes come in.
                  </Typography>
                </Alert>
              )}

              {/* Sorting Controls */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Sort by:
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label="Match Score"
                      onClick={() => handleSortChange('match_score')}
                      color={sortBy === 'match_score' ? 'primary' : 'default'}
                      variant={sortBy === 'match_score' ? 'filled' : 'outlined'}
                      icon={getSortIcon('match_score')}
                    />
                    <Chip
                      label="Distance"
                      onClick={() => handleSortChange('distance')}
                      color={sortBy === 'distance' ? 'primary' : 'default'}
                      variant={sortBy === 'distance' ? 'filled' : 'outlined'}
                      icon={getSortIcon('distance')}
                    />
                    <Chip
                      label="Name"
                      onClick={() => handleSortChange('name')}
                      color={sortBy === 'name' ? 'primary' : 'default'}
                      variant={sortBy === 'name' ? 'filled' : 'outlined'}
                      icon={getSortIcon('name')}
                    />
                  </Stack>
                </Box>

                {/* Provider Count Summary */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {vendors.length} provider{vendors.length !== 1 ? 's' : ''} found
                  </Typography>
                  {externalCalledVendors.length > 0 && (
                    <Chip
                      label={`${externalCalledVendors.length} being called`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Stack>

              {/* Provider List */}
              <Stack spacing={3}>
                {sortedResults.map(vendor => {
                  const vendorId = `${vendor.name}-${vendor.address}`;
                  const isSelected = selectedVendors.includes(vendorId);
                  const isCalled = externalCalledVendors.includes(vendorId);
                  return (
                    <Card
                      key={vendorId}
                      elevation={0}
                      sx={{
                        transition: 'all 0.3s ease',
                        border: '2px solid',
                        borderColor: (() => {
                          if (isCalled) return 'success.main';
                          if (isSelected) return 'primary.main';
                          return 'grey.200';
                        })(),
                        borderRadius: 3,
                        opacity: isCalled ? 0.9 : 1,
                        bgcolor: isCalled ? 'success.50' : 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        '&:hover': {
                          boxShadow: isCalled ? '0 6px 25px rgba(0,0,0,0.1)' : '0 8px 30px rgba(0,0,0,0.12)',
                          transform: isCalled ? 'translateY(-1px)' : 'translateY(-4px)',
                          borderColor: (() => {
                            if (isCalled) return 'success.main';
                            if (isSelected) return 'primary.main';
                            return 'grey.300';
                          })(),
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <Stack spacing={{ xs: 2.5, md: 3.5 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                              {isCalled ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 42,
                                    height: 42,
                                    borderRadius: 1,
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    mt: -1,
                                  }}
                                >
                                  <Phone01Icon width={20} height={20} />
                                </Box>
                              ) : (
                                <Checkbox
                                  checked={isSelected}
                                  onChange={e => handleVendorSelection(vendorId, e.target.checked)}
                                  sx={{ mt: -1 }}
                                />
                              )}
                              <Stack spacing={1.5} flex={1}>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  <Typography variant="h5" sx={{ 
                                    fontWeight: 700, 
                                    color: 'text.primary',
                                    letterSpacing: '-0.02em'
                                  }}>
                                    {vendor.name}
                                  </Typography>

                                  {/* Calling Status Indicator */}
                                  {isCalled && (
                                    <Chip
                                      icon={<Phone01Icon width={14} height={14} />}
                                      label="Terri is reaching out for quotes"
                                      size="small"
                                      color="success"
                                      sx={{
                                        fontWeight: 600,
                                        '& .MuiChip-label': {
                                          fontSize: '0.75rem',
                                        },
                                      }}
                                    />
                                  )}

                                  {/* Enhanced Distance Display - Prominent Position */}
                                  {vendor.distance && vendor.distance !== 'null' && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: 'primary.main',
                                          fontWeight: 700,
                                          fontSize: '1.1rem',
                                        }}
                                      >
                                        {formatDistanceDisplay(vendor)}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        away
                                      </Typography>
                                      {vendor.distance_meters !== null &&
                                      vendor.distance_meters !== undefined ? (
                                          <Chip
                                            label="Precise"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                          />
                                        ) : (
                                          <Chip
                                            label="Estimated"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                          />
                                        )}
                                    </Box>
                                  )}

                                  {vendor.emergency_service && (
                                    <Chip
                                      icon={<ZapFastIcon width={14} height={14} />}
                                      label="24/7 Emergency"
                                      size="small"
                                      color="error"
                                      sx={{
                                        height: 24,
                                        '& .MuiChip-label': {
                                          fontSize: '0.75rem',
                                          fontWeight: 600,
                                        },
                                      }}
                                    />
                                  )}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <MarkerPin01Icon width={16} height={16} />
                                  <Typography variant="body2" color="text.secondary">
                                    {vendor.address !== 'null'
                                      ? vendor.address
                                      : 'No address provided'}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Stack>
                            <Stack alignItems="flex-end" spacing={1}>
                              <Rating
                                value={vendor.match_score * 5}
                                max={5}
                                precision={0.5}
                                readOnly
                                size="medium"
                              />
                              <Typography variant="body2" color="text.secondary">
                                {(vendor.match_score * 100).toFixed(0)}% Match
                              </Typography>
                            </Stack>
                          </Stack>

                          <Divider sx={{ bgcolor: 'grey.100' }} />

                          <Stack spacing={2}>
                            <Stack spacing={1.5}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Services
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                color: 'text.secondary',
                                lineHeight: 1.6
                              }}>
                                {vendor.services}
                              </Typography>
                            </Stack>

                            {/* Contact Information */}
                            <Stack spacing={1.5}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Contact Information
                              </Typography>
                              <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Phone01Icon width={16} height={16} />
                                  <Typography variant="body2">{vendor.phone}</Typography>
                                </Stack>
                                {vendor.email && vendor.email !== 'null' && (
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Mail01Icon width={16} height={16} />
                                    <Typography
                                      variant="body2"
                                      component="a"
                                      href={`mailto:${vendor.email}`}
                                      sx={{
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {vendor.email}
                                    </Typography>
                                  </Stack>
                                )}
                                {vendor.website && vendor.website !== 'null' && (
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Globe02Icon width={16} height={16} />
                                    <Typography
                                      variant="body2"
                                      component="a"
                                      href={vendor.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {vendor.website}
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            </Stack>

                            <Stack spacing={1.5}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Why this provider?
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontStyle: 'italic'
                              }}>
                                {vendor.reason_for_recommendation}
                              </Typography>
                            </Stack>
                          </Stack>

                          {/* Call Provider Button / Status */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            {isCalled ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1.5,
                                  bgcolor: 'success.100',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'success.main',
                                }}
                              >
                                <CircularProgress size={16} sx={{ color: 'success.main' }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'success.main',
                                    fontWeight: 600,
                                  }}
                                >
                                  Terri is reaching out for quotes
                                </Typography>
                              </Box>
                            ) : (
                              <Button
                                variant={isSelected ? 'outlined' : 'contained'}
                                color="primary"
                                startIcon={
                                  isSelected ? null : <Phone01Icon width={18} height={18} />
                                }
                                onClick={() => handleVendorSelection(vendorId, !isSelected)}
                                sx={{
                                  minWidth: 140,
                                  textTransform: 'none',
                                  fontWeight: 500,
                                }}
                              >
                                {isSelected ? 'Provider Selected' : 'Call Provider'}
                              </Button>
                            )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </Container>

      {/* Floating Action Button for Calling Selected Providers */}
      {!isCallInProgress && selectedVendors.length > 0 && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleCallVendorsClick}
          disabled={isCallInProgress}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            px: 4,
            py: 2,
            borderRadius: 8,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
            },
          }}
        >
          {isCallInProgress ? (
            <>
              <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
              Initiating Calls...
            </>
          ) : (
            <>
              <Phone01Icon width={20} height={20} style={{ marginRight: 8 }} />
              Get Quotes from {selectedVendors.length} Provider{selectedVendors.length > 1 ? 's' : ''}
            </>
          )}
        </Fab>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Box>
  );
};

export default VendorSearchResults;
